
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const usePurchaseRequestDeletion = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      console.log('Deleting purchase request:', requestId);
      
      // Vérifier l'authentification
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('Utilisateur non authentifié');
      }

      // Vérifier que la demande appartient au professionnel avant suppression
      const { data: request, error: fetchError } = await supabase
        .from("product_purchase_requests")
        .select("id, professional_id")
        .eq("id", requestId)
        .single();

      if (fetchError) {
        console.error('Error fetching purchase request:', fetchError);
        throw new Error(`Erreur lors de la récupération de la demande: ${fetchError.message}`);
      }

      if (!request) {
        throw new Error('Demande d\'achat introuvable');
      }

      if (request.professional_id !== user.id) {
        throw new Error('Vous n\'avez pas l\'autorisation de supprimer cette demande');
      }

      // Effectuer la suppression
      const { error: deleteError } = await supabase
        .from("product_purchase_requests")
        .delete()
        .eq("id", requestId)
        .eq("professional_id", user.id); // Double vérification pour la sécurité

      if (deleteError) {
        console.error('Error deleting purchase request:', deleteError);
        throw new Error(`Erreur lors de la suppression: ${deleteError.message}`);
      }

      console.log('Successfully deleted purchase request');
    },
    onSuccess: () => {
      console.log('Deletion successful, invalidating queries');
      
      queryClient.invalidateQueries({ queryKey: ["purchase-requests"] });
      
      toast({
        title: "Succès",
        description: "La demande d'achat a été supprimée avec succès",
      });
    },
    onError: (error: Error) => {
      console.error("Detailed error deleting purchase request:", error);
      
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer la demande d'achat",
        variant: "destructive",
      });
    },
  });
};
